import React from 'react';
import { Flex, Text, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/formatters';
import RateSourceTooltip from './RateSourceTooltip';
import UnconvertibleNote from './UnconvertibleNote';

/**
 * MI-298: renders a single approximate converted total with an "≈" marker and,
 * when some movements had no rate, an "N not converted" disclosure. Presentational
 * only — pass the /movements/converted-total payload as `data`. A total whose
 * movements were all in the display currency is exact (MI-329): no "≈", and no
 * tooltip about rates.
 *
 * `magnitude` displays the absolute value (for spending surfaces that show
 * magnitudes); leave it off where the sign matters (net worth, assets vs debts).
 */
export default function ConvertedTotal({
  data,
  loading,
  magnitude = false,
  size = 'md',
  align = 'flex-end',
}) {
  const { t } = useTranslation('common');

  if (loading) return <Spinner size="xs" color="redesign.text.muted" />;
  if (!data) return null;

  const amount = magnitude ? Math.abs(data.total) : data.total;
  const amountFontSize = size === 'lg'
    ? { base: 'xl', md: '2xl' }
    : size === 'sm' ? 'sm' : 'md';

  return (
    <Flex direction="column" align={align} gap={0.5}>
      <RateSourceTooltip
        label={t('convert.approxTooltip')}
        sources={data.rate_sources}
        isDisabled={!data.is_converted}
      >
        <Text
          fontSize={amountFontSize}
          fontWeight={700}
          color="redesign.heading"
          whiteSpace="nowrap"
          cursor="default"
        >
          {data.is_converted && '≈ '}{formatMoney(amount, data.display_currency)}
        </Text>
      </RateSourceTooltip>
      {data.num_unconvertible > 0 && (
        <UnconvertibleNote
          buckets={data.unconvertible}
          count={data.num_unconvertible}
        />
      )}
    </Flex>
  );
}
